import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

// tools
import { hasRunningJob, isTerminalStatus } from '../helpers/aiJobs';

/** First poll after a job appears, then the interval backs off up to the cap. */
const POLL_START_MS = 5000;
const POLL_MAX_MS = 30000;
const POLL_GROWTH = 1.5;

/**
 * Lists the AI jobs of a project and keeps polling while any of them is still
 * running. There are no webhooks and no job-level GET, so the list endpoint is
 * the only way to learn that a job finished.
 *
 * Polling stops on its own the moment every job reached a terminal status, and
 * the interval widens as the wait grows so a long queue does not hammer the API.
 *
 * @param {object}   options
 * @param {array}    options.queryKey  react-query key for this list
 * @param {function} options.queryFn   returns the job array
 * @param {boolean}  options.enabled   usually "the modal is open"
 * @param {function} [options.select]  narrows the list (e.g. one generation mode)
 */
const useAiJobs = ({ queryKey, queryFn, enabled = true, select }) => {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey,
        queryFn: async () => {
            const list = await queryFn();

            return Array.isArray(list) ? list : [];
        },
        enabled,
        select,
        // Job state is the one thing that must never be served stale.
        staleTime: 0,
        refetchInterval: (queryInstance) => {
            const jobs = queryInstance.state.data;

            if (!Array.isArray(jobs) || !hasRunningJob(jobs)) return false;

            const polls = queryInstance.state.dataUpdateCount || 1;

            return Math.min(POLL_MAX_MS, POLL_START_MS * POLL_GROWTH ** (polls - 1));
        },
    });

    const refresh = useCallback(
        () => queryClient.invalidateQueries({ queryKey }),
        [queryClient, queryKey]
    );

    const jobs = query.data || [];

    return {
        jobs,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        error: query.error,
        refresh,
        // True while at least one job is queued or running, so the UI can say so.
        waiting: jobs.some(job => !isTerminalStatus(job.status)),
    };
}

export default useAiJobs;
